import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import '../styles/QuizAdmin.css';

// Icônes SVG inline
const IconHistory = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 3v18h18" />
    <path d="M19 9l-5 5-4-4-3 3" />
  </svg>
);

const IconCardinal = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
    <circle cx="9" cy="7" r="4"></circle>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
  </svg>
);

const IconRitual = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
    <path d="M2 17l10 5 10-5"></path>
    <path d="M2 12l10 5 10-5"></path>
  </svg>
);

const IconKeys = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="7.5" cy="15.5" r="4.5"></circle>
    <path d="M10.7 12.3 21 2"></path>
    <path d="m17 5 3 3"></path>
    <path d="m14 8 3 3"></path>
  </svg>
);

const IconGlobe = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="2" y1="12" x2="22" y2="12"></line>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
  </svg>
);

const IconEdit = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
  </svg>
);

const IconDelete = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
    <line x1="10" y1="11" x2="10" y2="17"></line>
    <line x1="14" y1="11" x2="14" y2="17"></line>
  </svg>
);

// Le back-office stocke une clé d'icône ; on la résout ici.
const ICONES = {
  keys: <IconKeys />,
  history: <IconHistory />,
  cardinal: <IconCardinal />,
  ritual: <IconRitual />,
  globe: <IconGlobe />
};
const iconePour = (cle) => ICONES[cle] || <IconHistory />;

const QuizAdmin = () => {
  // États pour la gestion des questions
  const [questions, setQuestions] = useState([]);
  const [themes, setThemes] = useState([]);
  const [nouveauTheme, setNouveauTheme] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // État pour le filtrage et la recherche
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('');
  
  // État pour l'édition
  const [editMode, setEditMode] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  
  // Configuration initiale d'une nouvelle question
  const emptyQuestion = {
    id: '',
    question: '',
    options: ['', '', '', ''],
    correctAnswer: 0,
    category: 'histoire',
    difficulty: 'débutant',
    explanation: ''
  };
  
  // Les quiz ne sont plus une liste figée : ils viennent de la base.
  
  const difficulties = [
    { id: 'débutant', name: 'Débutant', color: '#4caf50' },
    { id: 'intermédiaire', name: 'Intermédiaire', color: '#ff9800' },
    { id: 'expert', name: 'Expert', color: '#f44336' }
  ];
  
  // Charger les questions au démarrage
  useEffect(() => {
    fetchQuestions();
  }, []);
  
  // Charger les questions et la synthèse par thème depuis l'API.
  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const [resQuestions, resThemes] = await Promise.all([
        axios.get('/api/quiz'),
        axios.get('/api/quiz/themes/admin')
      ]);

      // L'API renvoie des documents Mongo (_id) ; toute la page raisonne en id.
      setQuestions((resQuestions.data || []).map(q => ({ ...q, id: q._id })));
      setThemes(Array.isArray(resThemes.data) ? resThemes.data : []);
      setError(null);
      setLoading(false);
    } catch (err) {
      console.error('Erreur lors du chargement des questions:', err);
      setError("Impossible de charger les questions. Vérifiez que vous êtes bien connecté.");
      setLoading(false);
    }
  };

  // Interrupteur du quiz : c'est cette valeur, et elle seule, qui décide de sa
  // présence sur la page publique.
  const basculerTheme = async (theme) => {
    try {
      await axios.put(`/api/quiz/themes/${theme._id}`, { actif: !theme.actif });
      await fetchQuestions();
      setSuccessMessage(`Quiz « ${theme.nom} » ${theme.actif ? 'désactivé' : 'activé'}`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Erreur lors du changement d\'état du quiz:', err);
      setError(err.response?.data?.msg || "Erreur lors du changement d'état du quiz");
      setTimeout(() => setError(null), 4000);
    }
  };

  const supprimerTheme = async (theme) => {
    if (!window.confirm(`Supprimer le quiz « ${theme.nom} » ?`)) return;
    try {
      await axios.delete(`/api/quiz/themes/${theme._id}`);
      await fetchQuestions();
      setSuccessMessage('Quiz supprimé');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Erreur lors de la suppression du quiz:', err);
      setError(err.response?.data?.msg || 'Erreur lors de la suppression du quiz');
      setTimeout(() => setError(null), 5000);
    }
  };

  const enregistrerNouveauTheme = async () => {
    if (!nouveauTheme?.nom?.trim() || !nouveauTheme?.slug?.trim()) {
      setError('Le nom et l\'identifiant du quiz sont requis');
      setTimeout(() => setError(null), 4000);
      return;
    }
    try {
      await axios.post('/api/quiz/themes', nouveauTheme);
      setNouveauTheme(null);
      await fetchQuestions();
      setSuccessMessage('Quiz créé');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Erreur lors de la création du quiz:', err);
      setError(err.response?.data?.msg || 'Erreur lors de la création du quiz');
      setTimeout(() => setError(null), 5000);
    }
  };

  // Propose un identifiant à partir du nom saisi : sans accent ni espace.
  const slugifier = (texte) => texte
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  // Filtrer les questions en fonction des critères
  const filteredQuestions = questions.filter(question => {
    const matchesSearch = question.question.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter ? question.category === categoryFilter : true;
    const matchesDifficulty = difficultyFilter ? question.difficulty === difficultyFilter : true;
    
    return matchesSearch && matchesCategory && matchesDifficulty;
  });
  
  // Créer une nouvelle question
  const createQuestion = () => {
    setCurrentQuestion({
      ...emptyQuestion,
      id: `q${Date.now()}` // Générer un ID unique basé sur l'horodatage
    });
    setEditMode(true);
  };
  
  // Éditer une question existante
  const editQuestion = (question) => {
    setCurrentQuestion({ ...question });
    setEditMode(true);
  };
  
  // Supprimer une question
  const deleteQuestion = async (questionId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette question ?')) {
      return;
    }

    try {
      await axios.delete(`/api/quiz/${questionId}`);
      await fetchQuestions();
      setSuccessMessage('Question supprimée avec succès');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Erreur lors de la suppression de la question:', err);
      setError(err.response?.data?.message || 'Erreur lors de la suppression de la question');
      setTimeout(() => setError(null), 4000);
    }
  };

  // Activer ou désactiver une question sans ouvrir le formulaire. C'est ce
  // levier qui permet de retirer un thème entier du quiz public.
  const toggleQuestionActive = async (question) => {
    try {
      await axios.put(`/api/quiz/${question.id}`, { ...question, active: !question.active });
      await fetchQuestions();
    } catch (err) {
      console.error('Erreur lors du changement d\'état:', err);
      setError(err.response?.data?.message || "Erreur lors du changement d'état");
      setTimeout(() => setError(null), 4000);
    }
  };

  // Activer ou désactiver toutes les questions d'un thème d'un coup.
  const toggleTheme = async (categoryId, activer) => {
    const concernees = questions.filter(q => q.category === categoryId && q.active !== activer);
    if (concernees.length === 0) return;
    if (!window.confirm(`${activer ? 'Activer' : 'Désactiver'} ${concernees.length} question(s) du thème ?`)) {
      return;
    }

    try {
      await Promise.all(
        concernees.map(q => axios.put(`/api/quiz/${q.id}`, { ...q, active: activer }))
      );
      await fetchQuestions();
      setSuccessMessage(`Thème ${activer ? 'activé' : 'désactivé'}`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Erreur lors du changement d\'état du thème:', err);
      setError("Erreur lors du changement d'état du thème");
      setTimeout(() => setError(null), 4000);
    }
  };

  // Annuler l'édition
  const cancelEdit = () => {
    setCurrentQuestion(null);
    setEditMode(false);
  };
  
  // Sauvegarder une question (nouvelle ou modifiée)
  const saveQuestion = async () => {
    // Validation des données
    if (!currentQuestion.question.trim()) {
      setError('Veuillez entrer une question');
      return;
    }
    
    if (currentQuestion.options.some(option => !option.trim())) {
      setError('Veuillez remplir toutes les options');
      return;
    }
    
    if (!currentQuestion.explanation.trim()) {
      setError('Veuillez entrer une explication');
      return;
    }
    
    try {
      const charge = {
        question: currentQuestion.question,
        options: currentQuestion.options,
        correctAnswer: currentQuestion.correctAnswer,
        category: currentQuestion.category,
        difficulty: currentQuestion.difficulty,
        explanation: currentQuestion.explanation,
        active: currentQuestion.active !== undefined ? currentQuestion.active : true
      };

      // Une question déjà enregistrée porte un _id Mongo ; sinon c'est une création.
      if (currentQuestion._id) {
        await axios.put(`/api/quiz/${currentQuestion._id}`, charge);
        setSuccessMessage('Question mise à jour avec succès');
      } else {
        await axios.post('/api/quiz', charge);
        setSuccessMessage('Nouvelle question créée avec succès');
      }

      await fetchQuestions();
      setCurrentQuestion(null);
      setEditMode(false);

      // Masquer le message après 3 secondes
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      console.error('Erreur lors de la sauvegarde de la question:', err);
      setError('Erreur lors de la sauvegarde de la question');
      
      // Masquer le message d'erreur après 3 secondes
      setTimeout(() => {
        setError(null);
      }, 3000);
    }
  };
  
  // Gérer les changements dans le formulaire d'édition
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentQuestion({
      ...currentQuestion,
      [name]: value
    });
  };
  
  // Gérer les changements dans les options
  const handleOptionChange = (index, value) => {
    const updatedOptions = [...currentQuestion.options];
    updatedOptions[index] = value;
    
    setCurrentQuestion({
      ...currentQuestion,
      options: updatedOptions
    });
  };
  
  // Formater le texte de la catégorie. Une question peut porter le slug d'un
  // quiz supprimé : on affiche alors le slug brut plutôt que rien.
  const getCategoryText = (categoryId) => {
    const theme = themes.find(t => t.slug === categoryId);
    return theme ? theme.nom : categoryId;
  };
  
  // Formater le texte de la difficulté
  const getDifficultyText = (difficultyId) => {
    const difficulty = difficulties.find(diff => diff.id === difficultyId);
    return difficulty ? difficulty.name : difficultyId;
  };
  
  // Obtenir la couleur de la difficulté
  const getDifficultyColor = (difficultyId) => {
    const difficulty = difficulties.find(diff => diff.id === difficultyId);
    return difficulty ? difficulty.color : '#999';
  };
  
  // Obtenir l'icône de la catégorie
  const getCategoryIcon = (categoryId) => {
    const theme = themes.find(t => t.slug === categoryId);
    return theme ? iconePour(theme.icone) : null;
  };
  
  return (
    <div className="quiz-admin-page">
      <h1>Administration du Quiz</h1>
      
      {/* Affichage des messages */}
      {error && <div className="message error">{error}</div>}
      {successMessage && <div className="message success">{successMessage}</div>}

      {/* Vue d'ensemble : un thème = un quiz. */}
      {!editMode && (
        <section className="quiz-overview">
          <div className="quiz-overview-head">
            <h2>Les quiz</h2>
            <button className="creer-theme" onClick={() => setNouveauTheme({ nom: '', slug: '', description: '', icone: 'history', actif: false })}>
              + Nouveau quiz
            </button>
          </div>

          {nouveauTheme && (
            <div className="theme-form">
              <div className="theme-form-champs">
                <label>
                  Nom
                  <input
                    type="text"
                    value={nouveauTheme.nom}
                    placeholder="Léon XIV"
                    onChange={e => setNouveauTheme({
                      ...nouveauTheme,
                      nom: e.target.value,
                      slug: slugifier(e.target.value)
                    })}
                  />
                </label>
                <label>
                  Identifiant
                  <input
                    type="text"
                    value={nouveauTheme.slug}
                    placeholder="leon-xiv"
                    onChange={e => setNouveauTheme({ ...nouveauTheme, slug: slugifier(e.target.value) })}
                  />
                  <small>Non modifiable ensuite : il relie les questions au quiz.</small>
                </label>
                <label className="theme-form-large">
                  Description
                  <input
                    type="text"
                    value={nouveauTheme.description}
                    placeholder="Affichée sous le titre, sur la page publique"
                    onChange={e => setNouveauTheme({ ...nouveauTheme, description: e.target.value })}
                  />
                </label>
                <label>
                  Icône
                  <select
                    value={nouveauTheme.icone}
                    onChange={e => setNouveauTheme({ ...nouveauTheme, icone: e.target.value })}
                  >
                    <option value="history">Histoire</option>
                    <option value="cardinal">Cardinaux</option>
                    <option value="ritual">Traditions</option>
                    <option value="globe">Géographie</option>
                    <option value="keys">Clés (papauté)</option>
                  </select>
                </label>
              </div>
              <div className="theme-form-actions">
                <button className="save-button" onClick={enregistrerNouveauTheme}>Créer</button>
                <button className="cancel-button" onClick={() => setNouveauTheme(null)}>Annuler</button>
              </div>
            </div>
          )}

          <div className="quiz-overview-table-wrap">
            <table className="quiz-overview-table">
              <thead>
                <tr>
                  <th>Quiz</th>
                  <th>Visible sur le site</th>
                  <th className="num">Questions</th>
                  <th>Répartition</th>
                  <th className="num">Parties</th>
                  <th className="num">Réussite</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {themes.length === 0 && (
                  <tr><td colSpan="7" className="aucun-theme">Aucun quiz. Créez-en un pour commencer.</td></tr>
                )}
                {themes.map(theme => {
                  const vide = theme.questionsActives === 0;
                  return (
                    <tr key={theme._id} className={theme.actif ? '' : 'theme-inactif'}>
                      <td className="theme-nom">
                        <span className="theme-icone">{iconePour(theme.icone)}</span>
                        <span>
                          {theme.nom}
                          <code className="theme-slug">{theme.slug}</code>
                        </span>
                      </td>
                      <td>
                        <button
                          className={`interrupteur${theme.actif ? ' on' : ''}`}
                          onClick={() => basculerTheme(theme)}
                          role="switch"
                          aria-checked={theme.actif}
                          title={theme.actif ? 'Retirer ce quiz du site' : 'Proposer ce quiz sur le site'}
                        >
                          <span className="interrupteur-piste"><span className="interrupteur-pastille" /></span>
                          <span className="interrupteur-libelle">{theme.actif ? 'Oui' : 'Non'}</span>
                        </button>
                        {theme.actif && vide && (
                          <span className="alerte-vide" title="Le quiz est annoncé mais aucune question active ne permet d'y jouer">
                            annoncé « bientôt »
                          </span>
                        )}
                      </td>
                      <td className="num">
                        <strong>{theme.questionsActives}</strong>
                        <span className="sur-total">/ {theme.questionsTotal}</span>
                      </td>
                      <td className="repartition">
                        {theme.questionsTotal > 0 ? (
                          <>
                            <span title="Débutant">{theme.difficultes['débutant']} déb.</span>
                            <span title="Intermédiaire">{theme.difficultes['intermédiaire']} int.</span>
                            <span title="Expert">{theme.difficultes['expert']} exp.</span>
                          </>
                        ) : <span className="vide">—</span>}
                      </td>
                      <td className="num">{theme.participations > 0 ? theme.participations : <span className="vide">—</span>}</td>
                      <td className="num">{theme.reussiteMoyenne !== null ? `${theme.reussiteMoyenne} %` : <span className="vide">—</span>}</td>
                      <td className="actions-theme">
                        <button
                          className="lien-action"
                          onClick={() => { setCategoryFilter(theme.slug); setSearchTerm(''); setDifficultyFilter(''); }}
                        >
                          Questions
                        </button>
                        {theme.questionsTotal === 0 && (
                          <button className="lien-action danger" onClick={() => supprimerTheme(theme)}>Supprimer</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <p className="quiz-overview-note">
            « Visible sur le site » est la seule chose qui décide de la présence d'un quiz
            sur la page publique. Un quiz visible mais sans question active y est annoncé
            comme « bientôt disponible » plutôt que d'être proposé puis de tomber en erreur.
            Le tirage prend 5 questions au hasard parmi les questions actives.
          </p>
        </section>
      )}

      {/* Mode édition */}
      {editMode ? (
        <div className="edit-question-section">
          <h2>{currentQuestion.id ? 'Modifier la question' : 'Créer une nouvelle question'}</h2>
          
          <div className="question-form">
            <div className="form-group">
              <label>Question</label>
              <input 
                type="text" 
                name="question" 
                value={currentQuestion.question} 
                onChange={handleInputChange} 
                placeholder="Entrez la question" 
                required 
              />
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label>Catégorie</label>
                <select 
                  name="category" 
                  value={currentQuestion.category} 
                  onChange={handleInputChange}
                >
                  {themes.length === 0 && <option value="">Créez d'abord un quiz</option>}
                  {themes.map(theme => (
                    <option key={theme._id} value={theme.slug}>
                      {theme.nom}{theme.actif ? '' : ' (masqué du site)'}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label>Difficulté</label>
                <select 
                  name="difficulty" 
                  value={currentQuestion.difficulty} 
                  onChange={handleInputChange}
                >
                  {difficulties.map(difficulty => (
                    <option key={difficulty.id} value={difficulty.id}>
                      {difficulty.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="options-section">
              <label>Options</label>
              {currentQuestion.options.map((option, index) => (
                <div key={index} className="option-group">
                  <div className="option-radio">
                    <input 
                      type="radio" 
                      name="correctAnswer" 
                      value={index} 
                      checked={currentQuestion.correctAnswer === index} 
                      onChange={() => setCurrentQuestion({...currentQuestion, correctAnswer: index})} 
                    />
                  </div>
                  <div className="option-letter">
                    {['A', 'B', 'C', 'D'][index]}
                  </div>
                  <input 
                    type="text" 
                    value={option} 
                    onChange={(e) => handleOptionChange(index, e.target.value)} 
                    placeholder={`Option ${['A', 'B', 'C', 'D'][index]}`} 
                    required 
                  />
                </div>
              ))}
              <p className="help-text">Sélectionnez la bonne réponse en cochant le bouton radio correspondant.</p>
            </div>
            
            <div className="form-group">
              <label>Explication</label>
              <textarea 
                name="explanation" 
                value={currentQuestion.explanation} 
                onChange={handleInputChange} 
                placeholder="Expliquez pourquoi cette réponse est correcte" 
                rows="3" 
                required 
              />
            </div>
            
            <div className="form-actions">
              <button 
                type="button" 
                className="save-button" 
                onClick={saveQuestion}
              >
                Enregistrer
              </button>
              <button 
                type="button" 
                className="cancel-button" 
                onClick={cancelEdit}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Filtres et barre de recherche */}
          <div className="admin-controls">
            <div className="search-bar">
              <input 
                type="text" 
                placeholder="Rechercher une question..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="filter-controls">
              <select 
                value={categoryFilter} 
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="">Toutes les catégories</option>
                {themes.map(theme => (
                  <option key={theme._id} value={theme.slug}>
                    {theme.nom}
                  </option>
                ))}
              </select>
              
              <select 
                value={difficultyFilter} 
                onChange={(e) => setDifficultyFilter(e.target.value)}
              >
                <option value="">Toutes les difficultés</option>
                {difficulties.map(difficulty => (
                  <option key={difficulty.id} value={difficulty.id}>
                    {difficulty.name}
                  </option>
                ))}
              </select>
              
              <button className="create-button" onClick={createQuestion}>
                + Nouvelle Question
              </button>
            </div>
          </div>
          
          {/* Liste des questions */}
          <div className="questions-list">
            {loading ? (
              <div className="loading">Chargement des questions...</div>
            ) : filteredQuestions.length === 0 ? (
              <div className="no-results">
                {searchTerm || categoryFilter || difficultyFilter ? 
                  "Aucune question ne correspond aux critères de recherche" : 
                  "Aucune question n'est disponible"}
              </div>
            ) : (
              filteredQuestions.map(question => (
                <div key={question.id} className="question-card">
                  <div className="question-header">
                    <div className="question-category">
                      <span className="category-icon">
                        {getCategoryIcon(question.category)}
                      </span>
                      <span className="category-text">
                        {getCategoryText(question.category)}
                      </span>
                    </div>
                    <div 
                      className="question-difficulty" 
                      style={{ 
                        backgroundColor: getDifficultyColor(question.difficulty),
                        opacity: 0.8
                      }}
                    >
                      {getDifficultyText(question.difficulty)}
                    </div>
                  </div>
                  
                  <div className="question-content">
                    <h3>{question.question}</h3>
                    
                    <div className="question-options">
                      {question.options.map((option, index) => (
                        <div 
                          key={index} 
                          className={`question-option ${index === question.correctAnswer ? 'correct-option' : ''}`}
                        >
                          <span className="option-letter">{['A', 'B', 'C', 'D'][index]}</span>
                          <span className="option-text">{option}</span>
                        </div>
                      ))}
                    </div>
                    
                    <div className="question-explanation">
                      <strong>Explication:</strong> {question.explanation}
                    </div>
                  </div>
                  
                  <div className="question-actions">
                    <button 
                      className="edit-button" 
                      onClick={() => editQuestion(question)}
                      title="Modifier cette question"
                    >
                      <IconEdit /> Modifier
                    </button>
                    <button
                      className="lien-action"
                      onClick={() => toggleQuestionActive(question)}
                      title={question.active ? 'Retirer cette question du tirage' : 'Remettre cette question dans le tirage'}
                    >
                      {question.active ? 'Désactiver' : 'Activer'}
                    </button>
                    <button
                      className="delete-button"
                      onClick={() => deleteQuestion(question.id)}
                      title="Supprimer cette question"
                    >
                      <IconDelete /> Supprimer
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default QuizAdmin;